import AppObj from 'model/appConfigObj';
import { TaskInitParams } from 'model/taskObj';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowRight,
  faDesktop,
  faDownload,
  faMagnifyingGlassMinus,
  faMagnifyingGlassPlus,
} from '@fortawesome/free-solid-svg-icons';
import imageViewerApi from './api';
import { desktopAppTaskContex } from 'screen/desktop';
import { isImageFile, isRasterImageFile, isRawFile } from 'util/openableFile';
import './imageViewer.css';

export const imageViewerConfig = {
  appID: 'imageViewer',
  showName: 'Image Viewer',
  onList: false,
  icon: require('./images/imageViewer.png'),
  maxHead: 8,
  defaultFullScreen: true,
  req: {
    minSize: {
      width: 420,
      height: 320,
    },
    defSize: {
      width: 760,
      height: 560,
    },
  },
};

const MIN_ZOOM = 0.1;
const MAX_ZOOM = 8;
const SWIPE_MIN_DX = 56;
const SWIPE_MAX_DY = 72;

function vibrateOnce() {
  try {
    navigator.vibrate?.(100);
  } catch {
    /* ignore */
  }
}

function basename(path: string): string {
  const cleaned = path.replace(/\/+$/, '');
  const idx = cleaned.lastIndexOf('/');
  return idx >= 0 ? cleaned.slice(idx + 1) : cleaned;
}

function parseInitParams(parms?: TaskInitParams[]) {
  let imagePath = '';
  let imageList: string[] = [];
  if (parms) {
    for (const p of parms) {
      if (p.command === 'imagePath' && p.value) {
        imagePath = p.value;
      }
      if (p.command === 'imageList' && p.value) {
        try {
          const parsed = JSON.parse(p.value);
          if (Array.isArray(parsed)) {
            imageList = parsed.filter((v) => typeof v === 'string');
          }
        } catch {
          /* ignore */
        }
      }
    }
  }
  if (imageList.length === 0 && imagePath) {
    imageList = [imagePath];
  }
  if (imagePath && !imageList.includes(imagePath)) {
    imageList = [imagePath, ...imageList];
  }
  if (!imagePath && imageList.length > 0) {
    imagePath = imageList[0];
  }
  return { imagePath, imageList };
}

function useAuthImage(path: string | null, preview: boolean) {
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!path) {
      setSrc(null);
      setError(null);
      return;
    }
    let objectUrl: string | null = null;
    let ticket = '';
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSrc(null);

    const fail = (e: any) => {
      if (!cancelled) {
        if (preview && (e?.response?.status === 400 || e?.response?.status === 422)) {
          setError('This image is too large or cannot be reduced');
          return;
        }
        setError(e?.response?.data?.message || e?.message || 'Failed to load image');
      }
    };

    const load = async () => {
      if (isRasterImageFile(path)) {
        const blob = preview
          ? await imageViewerApi.fetchImagePreview(path)
          : await imageViewerApi.fetchImageBlob(path);
        if (cancelled) {
          return;
        }
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
        return;
      }
      const res = await imageViewerApi.createMediaTicket(path);
      ticket = res.data?.data?.ticket || '';
      if (cancelled) {
        if (ticket) void imageViewerApi.revokeMediaTicket(ticket);
        return;
      }
      if (!res.data?.success || !ticket) {
        throw new Error(res.data?.message || 'Cannot open image');
      }
      setSrc(imageViewerApi.filesStreamUrl(ticket));
    };

    void load()
      .catch(fail)
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
      if (ticket) {
        void imageViewerApi.revokeMediaTicket(ticket);
      }
    };
  }, [path, preview]);

  return { src, loading, error };
}

async function copyImgToClipboard(img: HTMLImageElement) {
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('canvas');
  }
  ctx.drawImage(img, 0, 0);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) {
    throw new Error('blob');
  }
  await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
}

function ImageViewer(props: { taskInitParms?: TaskInitParams[]; taskID?: string }) {
  const [desktopControll] = useContext(desktopAppTaskContex);
  const init = useMemo(() => parseInitParams(props.taskInitParms), [props.taskInitParms]);
  const [paths, setPaths] = useState<string[]>(() =>
    init.imageList.filter((p) => isImageFile(basename(p)))
  );
  const [index, setIndex] = useState(() => {
    const start = init.imagePath || paths[0] || '';
    const idx = paths.indexOf(start);
    return idx >= 0 ? idx : 0;
  });
  const [zoom, setZoom] = useState(1);
  const [fitMode, setFitMode] = useState(true);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [preview, setPreview] = useState(true);
  const [copyHint, setCopyHint] = useState<string | null>(null);

  const stageRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const dragRef = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);
  const swipeRef = useRef<{ id: number; x: number; y: number } | null>(null);
  const copyHintTimer = useRef(0);
  const deletingRef = useRef(false);

  const currentPath = paths[index] || '';
  const raster = isRasterImageFile(currentPath);
  const raw = isRawFile(currentPath);
  const { src, loading, error } = useAuthImage(currentPath, raster && (preview || raw));
  const [decodeError, setDecodeError] = useState<string | null>(null);

  useEffect(() => {
    setDecodeError(null);
  }, [src]);

  useEffect(() => {
    if (raw) {
      setPreview(true);
    }
  }, [raw, currentPath]);

  useEffect(() => {
    if (!props.taskID || !currentPath) {
      return;
    }
    desktopControll?.setTaskTitle?.(props.taskID, basename(currentPath));
  }, [props.taskID, currentPath, desktopControll]);

  const applyFitZoom = useCallback(() => {
    const stage = stageRef.current;
    const img = imgRef.current;
    if (!stage || !img || !img.naturalWidth || !img.naturalHeight) {
      return;
    }
    const pad = 24;
    const availW = Math.max(1, stage.clientWidth - pad);
    const availH = Math.max(1, stage.clientHeight - pad);
    const scale = Math.min(availW / img.naturalWidth, availH / img.naturalHeight, 1);
    setZoom(scale);
    setPan({ x: 0, y: 0 });
    setFitMode(true);
  }, []);

  useEffect(() => {
    if (src) {
      applyFitZoom();
    }
  }, [src, currentPath, applyFitZoom]);

  const goPrev = useCallback(() => {
    setIndex((i) => (i > 0 ? i - 1 : i));
  }, []);

  const goNext = useCallback(() => {
    setIndex((i) => (i < paths.length - 1 ? i + 1 : i));
  }, [paths.length]);

  const onStageTouchStart = (ev: React.TouchEvent) => {
    if (ev.touches.length !== 1) {
      swipeRef.current = null;
      return;
    }
    const t = ev.touches[0];
    swipeRef.current = { id: t.identifier, x: t.clientX, y: t.clientY };
  };

  const onStageTouchEnd = (ev: React.TouchEvent) => {
    const start = swipeRef.current;
    swipeRef.current = null;
    if (!start || ev.touches.length > 0) {
      return;
    }
    const t = Array.from(ev.changedTouches).find((c) => c.identifier === start.id);
    if (!t) {
      return;
    }
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) < SWIPE_MIN_DX || Math.abs(dy) > SWIPE_MAX_DY || Math.abs(dx) < Math.abs(dy)) {
      return;
    }
    if (dx < 0) {
      if (index >= paths.length - 1) {
        return;
      }
      goNext();
    } else if (index <= 0) {
      return;
    } else {
      goPrev();
    }
    vibrateOnce();
  };

  const onStageTouchCancel = () => {
    swipeRef.current = null;
  };

  const zoomBy = useCallback((factor: number) => {
    setFitMode(false);
    setZoom((z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z * factor)));
  }, []);

  const showCopyHint = useCallback((msg: string) => {
    setCopyHint(msg);
    window.clearTimeout(copyHintTimer.current);
    copyHintTimer.current = window.setTimeout(() => setCopyHint(null), 1600);
  }, []);

  useEffect(() => () => window.clearTimeout(copyHintTimer.current), []);

  const download = () => {
    if (currentPath) {
      desktopControll?.launchFileDownload?.(currentPath);
    }
  };

  const setAsWallpaper = async () => {
    if (!currentPath || !raster) {
      return;
    }
    if (!window.confirm('Set this image as wallpaper?')) {
      return;
    }
    try {
      const res = await imageViewerApi.setWallpaper(currentPath);
      if (!res.data?.success) {
        throw new Error(res.data?.message || 'Failed');
      }
      showCopyHint('Wallpaper set');
      window.dispatchEvent(new Event('argon-wallpaper-changed'));
    } catch (e: any) {
      showCopyHint(e?.response?.data?.message || e?.message || 'Failed');
    }
  };

  const deleteCurrent = async () => {
    if (!currentPath || deletingRef.current) {
      return;
    }
    const name = basename(currentPath);
    if (!window.confirm(`Delete ${name}?`)) {
      return;
    }
    deletingRef.current = true;
    try {
      const res = await imageViewerApi.dsDelete(currentPath, false);
      if (!res.data.success) {
        throw new Error(res.data.message || 'Delete failed');
      }
      window.dispatchEvent(new CustomEvent('argon-file-deleted', { detail: { path: currentPath } }));
      const remaining = paths.filter((p) => p !== currentPath);
      if (remaining.length === 0) {
        if (props.taskID) {
          desktopControll?.killTask?.(props.taskID);
        }
        return;
      }
      setPaths(remaining);
      setIndex((i) => Math.min(i, remaining.length - 1));
    } catch (e: any) {
      showCopyHint(e?.response?.data?.message || e?.message || 'Delete failed');
    } finally {
      deletingRef.current = false;
    }
  };

  const onContextMenu = (ev: React.MouseEvent) => {
    ev.preventDefault();
    const img = imgRef.current;
    if (!img?.naturalWidth || loading || error || decodeError) {
      return;
    }
    void copyImgToClipboard(img)
      .then(() => showCopyHint('Copied'))
      .catch(() => showCopyHint('Copy failed'));
  };

  const onKeyDown = (ev: React.KeyboardEvent) => {
    if (ev.key === 'ArrowLeft') {
      ev.preventDefault();
      goPrev();
      return;
    }
    if (ev.key === 'ArrowRight') {
      ev.preventDefault();
      goNext();
      return;
    }
    if (ev.key === '+' || ev.key === '=') {
      ev.preventDefault();
      zoomBy(1.2);
      return;
    }
    if (ev.key === '-') {
      ev.preventDefault();
      zoomBy(1 / 1.2);
      return;
    }
    if (ev.key === '0') {
      ev.preventDefault();
      applyFitZoom();
      return;
    }
    if (ev.key === 'Delete') {
      if (ev.repeat) {
        return;
      }
      ev.preventDefault();
      void deleteCurrent();
    }
  };

  const onWheel = (ev: React.WheelEvent) => {
    ev.preventDefault();
    setFitMode(false);
    const factor = ev.deltaY < 0 ? 1.1 : 1 / 1.1;
    setZoom((z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z * factor)));
  };

  const onStageMouseDown = (ev: React.MouseEvent) => {
    if (ev.button !== 0 || fitMode) {
      return;
    }
    ev.preventDefault();
    setDragging(true);
    dragRef.current = {
      x: ev.clientX,
      y: ev.clientY,
      panX: pan.x,
      panY: pan.y,
    };

    const onMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d) {
        return;
      }
      setPan({
        x: d.panX + (e.clientX - d.x),
        y: d.panY + (e.clientY - d.y),
      });
    };

    const onUp = () => {
      dragRef.current = null;
      setDragging(false);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  if (paths.length === 0) {
    return (
      <div className="iv-root">
        <div className="iv-empty">No image to display</div>
      </div>
    );
  }

  const zoomPct = Math.round(zoom * 100);

  return (
    <div className="iv-root" tabIndex={0} onKeyDown={onKeyDown}>
      <div className="iv-toolbar">
        <div className="iv-toolbar-group">
          <button type="button" className="iv-btn" onClick={goPrev} disabled={index <= 0} title="Previous">
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <button
            type="button"
            className="iv-btn"
            onClick={goNext}
            disabled={index >= paths.length - 1}
            title="Next"
          >
            <FontAwesomeIcon icon={faArrowRight} />
          </button>
          <span className="iv-counter">
            {index + 1} / {paths.length}
          </span>
        </div>
        <div className="iv-toolbar-spacer" />
        <div className="iv-toolbar-group">
          <button type="button" className="iv-btn" onClick={() => zoomBy(1 / 1.2)} title="Zoom out">
            <FontAwesomeIcon icon={faMagnifyingGlassMinus} />
          </button>
          <span className="iv-counter">{zoomPct}%</span>
          <button type="button" className="iv-btn" onClick={() => zoomBy(1.2)} title="Zoom in">
            <FontAwesomeIcon icon={faMagnifyingGlassPlus} />
          </button>
          <button
            type="button"
            className="iv-btn active"
            onClick={() => void setAsWallpaper()}
            disabled={!raster || !src}
            title="Set as wallpaper"
          >
            <FontAwesomeIcon icon={faDesktop} />
          </button>
          <button type="button" className="iv-btn active" onClick={download} title="Download">
            <FontAwesomeIcon icon={faDownload} />
          </button>
        </div>
      </div>

      <div
        className={`iv-stage${dragging ? ' dragging' : ''}`}
        ref={stageRef}
        onWheel={onWheel}
        onMouseDown={onStageMouseDown}
        onDoubleClick={applyFitZoom}
        onContextMenu={onContextMenu}
        onTouchStart={onStageTouchStart}
        onTouchEnd={onStageTouchEnd}
        onTouchCancel={onStageTouchCancel}
      >
        {loading && <div className="iv-loading">Loading…</div>}
        {(error || decodeError) && !loading && <div className="iv-error">{error || decodeError}</div>}
        {copyHint && <div className="iv-copied">{copyHint}</div>}
        {!loading && !error && !decodeError && src && (
          <div className="iv-stage-inner">
            <img
              ref={imgRef}
              className="iv-image"
              src={src}
              alt={basename(currentPath)}
              draggable={false}
              onLoad={applyFitZoom}
              onError={() => setDecodeError('This image is too large to display')}
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              }}
            />
          </div>
        )}
      </div>

      <div className="iv-status">
        <span title={currentPath}>{currentPath}</span>
        {raster && (
          <div className="iv-status-size" role="group" aria-label="Image size">
            <button
              type="button"
              className={`iv-btn${preview ? ' active' : ''}`}
              onClick={() => setPreview(true)}
              title="Reduced JPEG, easier on weak PCs"
            >
              Small
            </button>
            {!raw && (
              <button
                type="button"
                className={`iv-btn${!preview ? ' active' : ''}`}
                onClick={() => setPreview(false)}
                title="Full original file"
              >
                Original
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const App: AppObj = {
  config: imageViewerConfig,
  body: ImageViewer,
};

export default App;
